import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { EChartsOption } from 'echarts';
import {
  IReitDataResponse,
  IReitProjectionLflNriGraphItem,
  ISeries,
} from '../../../interfaces';
import { formatPercentage } from '@kolytics/shared-functions';
import { colorPallete, GraphId } from '@kolytics/types/graph';
import { GraphComponent } from '../../../../../../../../../libs/shared-components/src/lib/components/graph/graph.component';
import { SectionComponent } from '../../../../../../../../../libs/shared-components/src/lib/components/section/section.component';

@Component({
  selector: 'klt-projection-lfl-nri',
  templateUrl: './projection-lfl-nri.component.html',
  styleUrls: ['./projection-lfl-nri.component.scss'],
  standalone: true,
  imports: [SectionComponent, GraphComponent],
})
export class ProjectionLflNriComponent {
  @Input() height = '415px';
  @Input() selectedScenarioName = 'base';
  @Input()
  public set reit(value: IReitDataResponse | null) {
    if (value) {
      this.reset(value.reits.reitProjection.lflNriGraph);
    }
  }
  @Input() loading: boolean | null = false;
  @Input() public options: EChartsOption | undefined = {
    grid: {
      show: true,
      borderWidth: 0,
      containLabel: true,
      top: 10,
      right: 10,
      bottom: 0,
      left: 5,
    },
    xAxis: {
      type: 'category',
      data: ['17A', '18A', '19A', '20A', '21F', '22F', '23F', '24F', '25F'],
      boundaryGap: false,
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        margin: 22,
      },
      splitArea: {
        show: true,
      },
      axisPointer: {
        show: true,
        type: 'none',
        label: {
          show: false,
        },
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: any) => {
          return formatPercentage(value, 1);
        },
        margin: 20,
      },
      axisLine: {
        show: false,
      },
      splitLine: {
        show: true,
      },
    },
    series: [],
    tooltip: {
      trigger: 'item',
      padding: 0,
      position: 'top',
      displayMode: 'single',
      confine: true,
      formatter: function (params: any) {
        if (params.componentType && params.componentType === 'series') {
          return `<div style="background: ${
            params.color
          }; color: var(--color--text-light); padding: var(--size--spacing-x2) var(--size--spacing-x4);">${
            params.name
          }: (${formatPercentage(params.value, 1)})</div>`;
        }
        return '';
      },
    },
  };

  public series: ISeries[] = [];

  public lines = [];

  public titles: {
    name: string;
    color: string;
    disabled: boolean;
    text?: string;
  }[] = [
    {
      name: 'NET RENTAL INCOME',
      text: `NET RENTAL INCOME (${this.selectedScenarioName})`,
      color: colorPallete[GraphId.ProjectionLflNri][0],
      disabled: false,
    },
  ];

  constructor(private cdr: ChangeDetectorRef) {}

  public runFilter(
    data: { name: string; color: string; disabled: boolean; text?: string }[],
  ): void {
    this.titles = data;

    this.titles[0].text = `NET RENTAL INCOME (${this.selectedScenarioName})`;

    const filter = data
      .filter((e) => !e.disabled)
      .map((e) => e.name.toLowerCase());
    const series = this.series.filter(
      (e) => e?.name && filter.includes(e.name.toString().toLowerCase()),
    );

    (this.options as EChartsOption).series = [
      ...(series as any),
      ...this.lines,
    ];

    const temp = Object.assign({}, this.options);

    this.options = undefined;

    setTimeout(() => {
      this.options = temp;
      this.cdr.detectChanges();
    }, 0);
  }

  public reset(value: IReitProjectionLflNriGraphItem[] = []): void {
    this.series = [];
    value = value.sort((a, b) => a.year - b.year);

    ((this.options as EChartsOption).xAxis as any).data = value.map(
      (e) => e.label,
    );

    const middle =
      value.length % 2 === 0
        ? value.length / 2 - 1
        : parseInt((value.length / 2).toString().split('.')[0], 0);

    const actuals = value.filter((e) => e.label.endsWith('A'));
    const lastActual = actuals[actuals.length - 1];

    this.series = [
      {
        data: value.map((e) => (e.year <= lastActual.year ? e.value : null)),
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: colorPallete[GraphId.ProjectionLflNri][0],
        name: 'NET RENTAL INCOME',
        lineStyle: {
          width: 4,
        },
      },

      // DASHED LINE
      {
        data: value.map((e) => (e.year >= lastActual.year ? e.value : null)),
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: colorPallete[GraphId.ProjectionLflNri][0],
        name: 'NET RENTAL INCOME',
        lineStyle: {
          type: 'dashed',
          width: 4,
        },
      },
    ] as ISeries[];
    this.runFilter(this.titles);
  }
}
